"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, User, Briefcase, LogOut } from "lucide-react";
import { getCurrentCustomer, getCurrentUser, getApiUrl, clearCustomerToken, setCustomerToken, requestLinkedCustomerToken, requestLinkedBusinessToken, type CustomerProfile, type AuthUser } from "@/lib/api";
import { toast } from "@/lib/toast";
import { refusalToastText } from "@/lib/account-link-copy";
import { clearToken, setToken } from "@/lib/auth";
import { CartUI } from "@/app/book/[businessSlug]/CartDrawer";

function getBusinessSlugFromPathname(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "book" || !segments[1]) return null;
  if (segments[1] === "demo") return null;
  return segments[1];
}

function avatarInitialsFromNameAndEmail(name: string | null, email: string): string {
  const n = name?.trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    if (parts[0].length >= 1) return parts[0].slice(0, 2).toUpperCase();
  }
  const e = email?.trim();
  if (e) return e.slice(0, 2).toUpperCase();
  return "?";
}

function avatarImageUrlFromProfile(profile: CustomerProfile): string | null {
  const img = profile.image;
  if (!img?.trim()) return null;
  if (img.startsWith("http")) return img;
  const base = getApiUrl();
  return `${base}/api${img.startsWith("/") ? "" : "/"}${img}`;
}

function avatarImageUrlFromUser(user: AuthUser): string | null {
  const img = user.image;
  if (!img?.trim()) return null;
  if (img.startsWith("http")) return img;
  const base = getApiUrl();
  return `${base}/api${img.startsWith("/") ? "" : "/"}${img}`;
}

export function BookPageNav() {
  const pathname = usePathname();
  const router = useRouter();
  const businessSlug = getBusinessSlugFromPathname(pathname ?? "");
  const isPublicBookingRoute = !!businessSlug;
  const [acceptsPayments, setAcceptsPayments] = useState(true);
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [businessUser, setBusinessUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  function loadAuth(showLoading = true) {
    if (showLoading) setAuthLoading(true);
    Promise.all([getCurrentCustomer(), getCurrentUser()])
      .then(([customer, business]) => {
        setCustomerProfile(customer ?? null);
        setBusinessUser(business?.businessId ? business : null);
      })
      .finally(() => setAuthLoading(false));
  }

  useEffect(() => {
    loadAuth(true);
  }, []);

  // Re-check auth when user returns to the tab or when pathname changes (e.g. after sign-in redirect)
  useEffect(() => {
    function onFocus() {
      loadAuth(false);
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  useEffect(() => {
    if (pathname) loadAuth(false);
  }, [pathname]);

  useEffect(() => {
    if (!businessSlug) return;
    fetch(`${getApiUrl()}/api/public/business/${businessSlug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => setAcceptsPayments(b?.acceptsPayments ?? true))
      .catch(() => setAcceptsPayments(true));
  }, [businessSlug]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [dropdownOpen]);

  const isCustomer = customerProfile != null;
  const hasBusiness = businessUser != null;

  // On public booking routes, suppress business/dashboard chrome so owners
  // see the same customer-facing UI as any visitor.
  const showBusinessChrome = hasBusiness && !isPublicBookingRoute;
  const ownsViewedBusiness =
    isPublicBookingRoute &&
    hasBusiness &&
    businessUser!.business?.slug === businessSlug;

  const isSignedIn = isCustomer || showBusinessChrome;
  // Offer a passwordless switch only when the API says it would work (same rule as the link endpoints)
  const canUseAsCustomer = businessUser?.canSwitchToCustomer !== false;
  const canSwitchToBusiness = customerProfile?.canSwitchToBusiness === true;
  const displayName = customerProfile?.name ?? (showBusinessChrome ? businessUser?.name : null) ?? "Account";
  const displayEmail = customerProfile?.email ?? (showBusinessChrome ? businessUser?.email : null) ?? "";
  const displayInitials = isCustomer
    ? avatarInitialsFromNameAndEmail(customerProfile!.name, customerProfile!.email)
    : showBusinessChrome && businessUser
      ? avatarInitialsFromNameAndEmail(businessUser.name, businessUser.email)
      : "?";
  const displayImageUrl =
    (isCustomer && avatarImageUrlFromProfile(customerProfile!)) ||
    (showBusinessChrome && businessUser && avatarImageUrlFromUser(businessUser)) ||
    null;

  function handleSignOut() {
    clearCustomerToken();
    clearToken();
    setDropdownOpen(false);
    setMobileOpen(false);
    router.push("/");
    router.refresh();
  }

  async function handleUseAsCustomer() {
    if (linkLoading) return;
    setLinkLoading(true);
    try {
      const result = await requestLinkedCustomerToken();
      const { token, refusedMessage } = result;
      if (token) {
        setCustomerToken(token);
        loadAuth(false);
        setDropdownOpen(false);
      } else if (refusedMessage) {
        // Say why and how to fix it (confirm the link, verify an email, or wait)
        toast.error(refusalToastText("business", refusedMessage, result.code, result.retryAfterSeconds));
        setDropdownOpen(false);
      } else {
        router.push(loginUrl);
      }
    } finally {
      setLinkLoading(false);
    }
  }

  async function handleSwitchToBusiness() {
    if (linkLoading) return;
    setLinkLoading(true);
    try {
      const result = await requestLinkedBusinessToken();
      const { token, refusedMessage } = result;
      if (token) {
        setToken(token);
        setDropdownOpen(false);
        setMobileOpen(false);
        router.push("/app/dashboard");
        router.refresh();
      } else if (refusedMessage) {
        toast.error(refusalToastText("customer", refusedMessage, result.code, result.retryAfterSeconds));
        setDropdownOpen(false);
      } else {
        router.push("/auth/login");
      }
    } finally {
      setLinkLoading(false);
    }
  }

  const loginUrl = pathname ? `/auth/customer/login?callbackUrl=${encodeURIComponent(pathname)}` : "/auth/customer/login";

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4 min-w-0">
        <Link href="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity shrink-0">
          <Image src="/logo192.png" alt="" width={28} height={28} className="rounded" aria-hidden />
          <span className="text-sm font-semibold text-foreground tracking-tight">Book4</span>
        </Link>
        {/* Desktop nav */}
        <nav className="hidden sm:flex items-center gap-4 shrink-0">
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
            Home
          </Link>
          {ownsViewedBusiness && (
            <Link
              href="/app/dashboard"
              className="text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors whitespace-nowrap"
            >
              Manage
            </Link>
          )}
          {businessSlug && (
            <div className="shrink-0">
              <CartUI businessSlug={businessSlug} acceptsPayments={acceptsPayments} />
            </div>
          )}
          {authLoading ? (
            <div className="h-9 w-[calc(2rem+6px)] rounded-full bg-muted/50 animate-pulse shrink-0" aria-hidden />
          ) : isSignedIn ? (
            <div ref={dropdownRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-full p-0.5 border border-border bg-muted/50 hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="Account menu"
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                {displayImageUrl ? (
                  <img
                    src={displayImageUrl}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover"
                    aria-hidden
                  />
                ) : (
                  <span
                    className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold"
                    aria-hidden
                  >
                    {displayInitials}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {dropdownOpen && (
                <div
                  className="absolute right-0 mt-1.5 w-56 rounded-lg border border-border bg-card shadow-lg py-1 z-50"
                  role="menu"
                >
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>
                  </div>
                  {isCustomer && (
                    <Link
                      href="/customer"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/80 transition-colors"
                      role="menuitem"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <User className="w-4 h-4 text-muted-foreground" />
                      My account
                    </Link>
                  )}
                  {showBusinessChrome && (
                    <Link
                      href="/app/dashboard"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/80 transition-colors"
                      role="menuitem"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                      Business
                    </Link>
                  )}
                  {showBusinessChrome && !isCustomer && canUseAsCustomer && (
                    <button
                      type="button"
                      onClick={handleUseAsCustomer}
                      disabled={linkLoading}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/80 transition-colors w-full text-left disabled:opacity-50"
                      role="menuitem"
                    >
                      <User className="w-4 h-4 text-muted-foreground shrink-0" />
                      {linkLoading ? "Loading…" : "Use as customer"}
                    </button>
                  )}
                  {isCustomer && canSwitchToBusiness && (showBusinessChrome || customerProfile?.hasBusinessAccess) && (
                    <button
                      type="button"
                      onClick={handleSwitchToBusiness}
                      disabled={linkLoading}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted/80 transition-colors w-full text-left disabled:opacity-50"
                      role="menuitem"
                    >
                      <Briefcase className="w-4 h-4 text-muted-foreground shrink-0" />
                      {linkLoading ? "Loading…" : "Switch to business"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors text-left"
                    role="menuitem"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              href={loginUrl}
              className="text-sm font-medium rounded-lg border border-border bg-background px-4 py-2 hover:bg-muted/80 transition-colors"
            >
              Sign in
            </Link>
          )}
        </nav>
        {/* Mobile: cart + menu button */}
        <div className="flex items-center gap-2 sm:hidden shrink-0">
          {businessSlug && (
            <div className="shrink-0">
              <CartUI businessSlug={businessSlug} acceptsPayments={acceptsPayments} />
            </div>
          )}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="sm:hidden border-t border-border bg-card">
          <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
            <Link
              href="/"
              className="px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Home
            </Link>
            {ownsViewedBusiness && (
              <Link
                href="/app/dashboard"
                className="px-3 py-2.5 rounded-lg text-xs text-muted-foreground/70 hover:bg-muted/80 hover:text-muted-foreground transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Manage this business
              </Link>
            )}
            {authLoading ? (
              <div className="h-12 rounded-lg bg-muted/50 animate-pulse w-24" aria-hidden />
            ) : isSignedIn ? (
              <>
                <div className="px-3 py-2 flex items-center gap-3 border-b border-border">
                  {displayImageUrl ? (
                    <img
                      src={displayImageUrl}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="w-10 h-10 rounded-full bg-primary/15 text-primary flex items-center justify-center text-sm font-semibold">
                      {displayInitials}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{displayEmail}</p>
                  </div>
                </div>
                {isCustomer && (
                  <Link
                    href="/customer"
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted/80 transition-colors flex items-center gap-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    My account
                  </Link>
                )}
                {showBusinessChrome && (
                  <Link
                    href="/app/dashboard"
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted/80 transition-colors flex items-center gap-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    <Briefcase className="w-4 h-4" />
                    Business
                  </Link>
                )}
                {showBusinessChrome && !isCustomer && canUseAsCustomer && (
                  <button
                    type="button"
                    onClick={() => {
                      handleUseAsCustomer();
                      setMobileOpen(false);
                    }}
                    disabled={linkLoading}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted/80 transition-colors flex items-center gap-2 w-full text-left disabled:opacity-50"
                  >
                    <User className="w-4 h-4 shrink-0" />
                    {linkLoading ? "Loading…" : "Use as customer"}
                  </button>
                )}
                {isCustomer && canSwitchToBusiness && (showBusinessChrome || customerProfile?.hasBusinessAccess) && (
                  <button
                    type="button"
                    onClick={() => {
                      handleSwitchToBusiness();
                      setMobileOpen(false);
                    }}
                    disabled={linkLoading}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted/80 transition-colors flex items-center gap-2 w-full text-left disabled:opacity-50"
                  >
                    <Briefcase className="w-4 h-4 shrink-0" />
                    {linkLoading ? "Loading…" : "Switch to business"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    handleSignOut();
                  }}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors flex items-center gap-2 w-full text-left"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </>
            ) : (
              <Link
                href={loginUrl}
                className="px-3 py-2.5 rounded-lg text-sm font-medium border border-border bg-background hover:bg-muted/80 transition-colors inline-flex w-fit"
                onClick={() => setMobileOpen(false)}
              >
                Sign in
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
